import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import { loadConfig, saveConfigSlice } from "./storage";
import { ADMIN_DEMO_STORAGE_KEY, type AdminDemoConfig } from "./types";

type AdminDemoContextValue = {
  config: AdminDemoConfig;
  updateConfig: <K extends keyof AdminDemoConfig>(
    key: K,
    value: AdminDemoConfig[K],
  ) => void;
};

const AdminDemoContext = createContext<AdminDemoContextValue>({
  config: {},
  updateConfig: () => {},
});

export function useAdminDemo(): AdminDemoContextValue {
  return useContext(AdminDemoContext);
}

export const AdminDemoProvider = memo(function AdminDemoProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [config, setConfig] = useState<AdminDemoConfig>(loadConfig);

  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === ADMIN_DEMO_STORAGE_KEY) {
        setConfig(loadConfig());
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const updateConfig = useCallback(
    <K extends keyof AdminDemoConfig>(key: K, value: AdminDemoConfig[K]) => {
      const next = saveConfigSlice(key, value);
      setConfig(next);
    },
    [],
  );

  return (
    <AdminDemoContext.Provider value={{ config, updateConfig }}>
      {children}
    </AdminDemoContext.Provider>
  );
});
